/**
 * useParentQuickRead Hook
 *
 * Provides a one-click "Quick Read" experience for parents reading to their children.
 * Uses the recommendation system to find the best next story for the selected child.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { recommendationsApi, QuickRecommendationResponse } from '@/lib/api-client';

export type ParentQuickReadState = 'idle' | 'loading' | 'generating' | 'error';

interface UseParentQuickReadResult {
  /** Trigger the quick read flow for the selected child */
  startQuickRead: () => void;
  /** Current state of the quick read process */
  state: ParentQuickReadState;
  /** Error message if state is 'error' */
  error: string | null;
  /** Whether the button should be disabled */
  isDisabled: boolean;
  /** Recommendation data (available after successful fetch) */
  recommendation: QuickRecommendationResponse | null;
}

interface UseParentQuickReadOptions {
  /** The student ID of the child to get recommendations for */
  studentId: string | null;
  /** Name of the child (for display in notifications) */
  childName?: string;
}

export function useParentQuickRead({
  studentId,
  childName,
}: UseParentQuickReadOptions): UseParentQuickReadResult {
  const navigate = useNavigate();
  const [state, setState] = useState<ParentQuickReadState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [recommendation, setRecommendation] = useState<QuickRecommendationResponse | null>(null);

  const quickReadMutation = useMutation({
    mutationFn: () => {
      if (!studentId) {
        throw new Error('No child selected');
      }
      return recommendationsApi.getQuickRecommendationForStudent(studentId);
    },
    onMutate: () => {
      setState('loading');
      setError(null);
    },
    onSuccess: (data) => {
      setRecommendation(data);
      const displayName = childName || 'your child';

      if (data.type === 'existing_story' && data.story_id) {
        // studentId in the query string tells the reader "I'm reading with
        // this kid" — DictionaryPopover then credits any lookups to the
        // student's vocab deck instead of the parent's account.
        navigate(`/story/${data.story_id}?studentId=${studentId}`);
        setState('idle');

        notifications.show({
          title: `Story ready for ${displayName}`,
          message: data.reason,
          color: 'teal',
        });
      } else if (data.type === 'generate_new' && data.theme_id) {
        setState('generating');
        navigate('/library/story/create', {
          state: {
            themeId: data.theme_id,
            fromQuickRead: true,
            forStudentId: studentId,
          },
        });

        notifications.show({
          title: `Creating a new story for ${displayName}`,
          message: data.reason,
          color: 'blue',
        });

        setTimeout(() => setState('idle'), 1000);
      } else {
        setState('idle');
        notifications.show({
          title: 'No stories available',
          message: data.reason || 'Try generating a new story or check back later.',
          color: 'yellow',
        });
      }
    },
    onError: (err: Error) => {
      setState('error');
      setError(err.message);

      notifications.show({
        title: 'Could not find a recommendation',
        message: err.message || 'Please try again later.',
        color: 'red',
      });
    },
  });

  const startQuickRead = useCallback(() => {
    if (!studentId) {
      notifications.show({
        title: 'No child selected',
        message: 'Please select a child first.',
        color: 'yellow',
      });
      return;
    }
    quickReadMutation.mutate();
  }, [quickReadMutation, studentId]);

  return {
    startQuickRead,
    state,
    error,
    isDisabled: !studentId || state === 'loading' || state === 'generating',
    recommendation,
  };
}
